import { Expert, ExpertField, EXPERTS_DATA } from '@/models/Expert';
import { supabase } from '@/services/supabase';
import { v4 as uuidv4 } from 'uuid';

// Thêm cache đơn giản để lưu trữ chuyên gia đã fetch trước đó
const expertCache: { [key: string]: { expert: Expert | null, timestamp: number } } = {};
const CACHE_TTL = 60 * 1000; // 60 giây

/**
 * Service quản lý chuyên gia
 */
export const expertService = {
  /**
   * Lấy tất cả chuyên gia
   * @returns Danh sách chuyên gia
   */
  async getAllExperts(): Promise<Expert[]> {
    try {
      console.log('Fetching all experts');
      
      // Thử lấy từ Supabase trước
      const { data, error } = await supabase
        .from('experts')
        .select('*')
        .order('name');
      
      if (error) {
        console.error('Error fetching experts from Supabase:', error);
        // Fallback: Trả về dữ liệu mẫu
        console.log('Fallback: Using sample experts data');
        return EXPERTS_DATA;
      }
      
      if (data && data.length > 0) {
        console.log(`Found ${data.length} experts in Supabase`);
        // Chuyển đổi từ snake_case sang camelCase
        const dbExperts = data.map(expert => ({
          id: expert.id,
          name: expert.name,
          title: expert.title,
          field: expert.field,
          avatar: expert.avatar,
          description: expert.description,
          personality: expert.personality,
          greeting: expert.greeting,
          systemPrompt: expert.system_prompt,
          promptPrefix: expert.prompt_prefix,
          active: expert.active,
          createdAt: expert.created_at,
          updatedAt: expert.updated_at
        }));
        
        // Kết hợp dữ liệu từ DB và dữ liệu mẫu, ưu tiên dữ liệu từ DB
        const dbExpertIds = dbExperts.map(e => e.id);
        const sampleExpertsNotInDB = EXPERTS_DATA.filter(e => !dbExpertIds.includes(e.id));
        
        return [...dbExperts, ...sampleExpertsNotInDB];
      }
      
      // Nếu không có dữ liệu từ Supabase, trả về dữ liệu mẫu
      console.log('No experts found in Supabase, using sample data');
      return EXPERTS_DATA;
    } catch (error) {
      console.error('Error in getAllExperts:', error);
      // Fallback: Trả về dữ liệu mẫu
      return EXPERTS_DATA;
    }
  },
  
  /**
   * Lấy chuyên gia theo ID
   * @param id ID của chuyên gia
   * @returns Chuyên gia hoặc null nếu không tìm thấy
   */
  async getExpertById(id: string): Promise<Expert | null> {
    try {
      // Nếu ID không hợp lệ, trả về null ngay lập tức
      if (!id) {
        return null;
      }
      
      // Kiểm tra xem có trong cache không và cache còn hạn dùng không
      const now = Date.now();
      if (expertCache[id] && now - expertCache[id].timestamp < CACHE_TTL) {
        return expertCache[id].expert;
      }
      
      // Ghi nhận lấy expert
      console.log(`Getting expert by ID: ${id}`);
      
      // Trường hợp đặc biệt: ID là "default" - lấy chuyên gia general hoặc default từ mẫu
      if (id === "default") {
        // Kiểm tra xem có trong cache không
        if (expertCache["default"]) {
          return expertCache["default"].expert;
        }
        
        // Tìm trong mẫu trước để tránh gọi API không cần thiết
        const defaultExpertFromSample = EXPERTS_DATA.find(expert => expert.id === "default");
        if (defaultExpertFromSample) {
          // Lưu vào cache
          expertCache["default"] = { expert: defaultExpertFromSample, timestamp: now };
          return defaultExpertFromSample;
        }
        
        console.log("Getting default expert with field=general");
        const { data, error } = await supabase
          .from('experts')
          .select('*')
          .eq('field', ExpertField.GENERAL)
          .limit(1)
          .single();
        
        if (error || !data) {
          const fallbackExpert = EXPERTS_DATA[0];
          // Lưu vào cache
          expertCache["default"] = { expert: fallbackExpert, timestamp: now };
          return fallbackExpert;
        }
        
        const expert = {
          id: data.id,
          name: data.name,
          title: data.title,
          field: data.field,
          avatar: data.avatar,
          description: data.description,
          personality: data.personality,
          greeting: data.greeting,
          systemPrompt: data.system_prompt,
          promptPrefix: data.prompt_prefix,
          active: data.active,
          createdAt: data.created_at,
          updatedAt: data.updated_at
        };
        
        // Lưu vào cache
        expertCache["default"] = { expert, timestamp: now };
        console.log(`Found default expert: ${data.name}`);
        return expert;
      }
      
      // Kiểm tra xem ID có phải là UUID hợp lệ không
      const isValidUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(id);
      
      // Nếu không phải UUID hợp lệ, tìm trong dữ liệu mẫu
      if (!isValidUUID) {
        console.warn(`ID ${id} is not a valid UUID, looking in sample data instead`);
        const localExpert = EXPERTS_DATA.find(expert => expert.id === id);
        
        // Lưu vào cache
        expertCache[id] = { expert: localExpert || null, timestamp: now };
        return localExpert || null;
      }
      
      // Thử lấy từ Supabase
      const { data, error } = await supabase
        .from('experts')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) {
        console.error(`Error fetching expert with ID ${id} from Supabase:`, error);
        // Fallback: Tìm trong dữ liệu mẫu
        const localExpert = EXPERTS_DATA.find(expert => expert.id === id);
        
        // Lưu vào cache
        expertCache[id] = { expert: localExpert || null, timestamp: now };
        return localExpert || null;
      }
      
      if (data) {
        // Chuyển đổi từ snake_case sang camelCase
        const expert = {
          id: data.id,
          name: data.name,
          title: data.title,
          field: data.field,
          avatar: data.avatar,
          description: data.description,
          personality: data.personality,
          greeting: data.greeting,
          systemPrompt: data.system_prompt,
          promptPrefix: data.prompt_prefix,
          active: data.active,
          createdAt: data.created_at,
          updatedAt: data.updated_at
        };
        
        // Lưu vào cache
        expertCache[id] = { expert, timestamp: now };
        return expert;
      }
      
      // Nếu không tìm thấy, tìm trong dữ liệu mẫu
      const localExpert = EXPERTS_DATA.find(expert => expert.id === id);
      
      // Lưu vào cache
      expertCache[id] = { expert: localExpert || null, timestamp: now };
      return localExpert || null;
    } catch (error) {
      console.error(`Error in getExpertById for ID ${id}:`, error);
      // Fallback: Tìm trong dữ liệu mẫu
      const localExpert = EXPERTS_DATA.find(expert => expert.id === id);
      return localExpert || null;
    }
  },
  
  /**
   * Xóa cache cho một expert hoặc tất cả cache
   * @param id ID của expert cần xóa cache, nếu không truyền thì xóa tất cả
   */
  clearExpertCache(id?: string) {
    if (id) {
      delete expertCache[id];
    } else {
      // Xóa tất cả cache
      Object.keys(expertCache).forEach(key => {
        delete expertCache[key];
      });
    }
  },
  
  /**
   * Lấy chuyên gia mặc định cho một lĩnh vực
   * @param field Lĩnh vực cần lấy chuyên gia
   * @returns Chuyên gia mặc định cho lĩnh vực
   */
  async getDefaultExpertForField(field: ExpertField): Promise<Expert> {
    try {
      console.log(`Getting default expert for field: ${field}`);
      
      // Thử lấy từ Supabase trước
      const { data, error } = await supabase
        .from('experts')
        .select('*')
        .eq('field', field)
        .eq('active', true)
        .limit(1)
        .single();
      
      if (error) {
        console.error(`Error fetching default expert for field ${field} from Supabase:`, error);
        // Fallback: Tìm trong dữ liệu mẫu
        console.log(`Fallback: Looking for default expert for field ${field} in sample data`);
        const localExpert = EXPERTS_DATA.find(expert => expert.field === field);
        if (localExpert) {
          console.log(`Found default expert ${localExpert.name} for field ${field} in sample data`);
          return localExpert;
        }
        
        // Nếu không tìm thấy, trả về chuyên gia mặc định
        console.log(`No default expert found for field ${field}, using DEFAULT_EXPERT`);
        return EXPERTS_DATA[0]; // Chuyên gia đầu tiên trong danh sách mẫu
      }
      
      if (data) {
        console.log(`Found default expert ${data.name} for field ${field} in Supabase`);
        // Chuyển đổi từ snake_case sang camelCase
        return {
          id: data.id,
          name: data.name,
          title: data.title,
          field: data.field,
          avatar: data.avatar,
          description: data.description,
          personality: data.personality,
          greeting: data.greeting,
          systemPrompt: data.system_prompt,
          promptPrefix: data.prompt_prefix,
          active: data.active,
          createdAt: data.created_at,
          updatedAt: data.updated_at
        };
      }
      
      // Nếu không tìm thấy, tìm trong dữ liệu mẫu
      console.log(`No default expert for field ${field} found in Supabase, looking in sample data`);
      const localExpert = EXPERTS_DATA.find(expert => expert.field === field);
      if (localExpert) {
        console.log(`Found default expert ${localExpert.name} for field ${field} in sample data`);
        return localExpert;
      }
      
      // Nếu vẫn không tìm thấy, trả về chuyên gia mặc định
      console.log(`No default expert found for field ${field}, using DEFAULT_EXPERT`);
      return EXPERTS_DATA[0];
    } catch (error) {
      console.error(`Error in getDefaultExpertForField for field ${field}:`, error);
      // Fallback: Trả về chuyên gia mặc định
      return EXPERTS_DATA[0];
    }
  },
  
  /**
   * Tạo hoặc cập nhật chuyên gia
   * @param expert Thông tin chuyên gia
   * @returns Chuyên gia đã được tạo hoặc cập nhật
   */
  async saveExpert(expert: Expert): Promise<Expert> {
    try {
      console.log(`Saving expert: ${expert.name}`);
      
      // Tạo UUID mới nếu ID không phải là UUID hợp lệ
      const isValidUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(expert.id);
      const expertId = isValidUUID ? expert.id : uuidv4();
      
      if (!isValidUUID) {
        console.warn(`Expert ID ${expert.id} is not a valid UUID, generating new UUID: ${expertId}`);
      }
      
      // Chuyển đổi từ camelCase sang snake_case
      const expertData = {
        id: expertId,
        name: expert.name,
        title: expert.title,
        field: expert.field,
        avatar: expert.avatar,
        description: expert.description,
        personality: expert.personality,
        greeting: expert.greeting,
        system_prompt: expert.systemPrompt,
        prompt_prefix: expert.promptPrefix,
        active: expert.active
      };
      
      // Kiểm tra xem chuyên gia đã tồn tại chưa
      const { data: existingExpert, error: checkError } = await supabase
        .from('experts')
        .select('id')
        .eq('id', expertId)
        .single();
      
      if (checkError && checkError.code !== 'PGRST116') { // PGRST116 là lỗi "không tìm thấy"
        console.error(`Error checking if expert with ID ${expertId} exists:`, checkError);
      }
      
      // Cập nhật thời gian
      const now = new Date().toISOString();
      const updatedExpertData = {
        ...expertData,
        updated_at: now
      };
      
      if (!existingExpert) {
        // Tạo mới nếu chưa tồn tại
        const newExpertData = {
          ...updatedExpertData,
          created_at: now
        };
        
        const { data, error } = await supabase
          .from('experts')
          .insert(newExpertData)
          .select()
          .single();
        
        if (error) {
          console.error(`Error creating expert with ID ${expertId}:`, error);
          return { ...expert, id: expertId };
        }
        
        // Xóa cache cho expert này
        this.clearExpertCache(expertId);
        
        // Chuyển đổi từ snake_case sang camelCase
        return {
          id: data.id,
          name: data.name,
          title: data.title,
          field: data.field,
          avatar: data.avatar,
          description: data.description,
          personality: data.personality,
          greeting: data.greeting,
          systemPrompt: data.system_prompt,
          promptPrefix: data.prompt_prefix,
          active: data.active,
          createdAt: data.created_at,
          updatedAt: data.updated_at
        };
      } else {
        // Cập nhật nếu đã tồn tại
        const { data, error } = await supabase
          .from('experts')
          .update(updatedExpertData)
          .eq('id', expertId)
          .select()
          .single();
        
        if (error) {
          console.error(`Error updating expert with ID ${expertId}:`, error);
          return { ...expert, id: expertId };
        }
        
        // Xóa cache cho expert này
        this.clearExpertCache(expertId);
        
        // Chuyển đổi từ snake_case sang camelCase
        return {
          id: data.id,
          name: data.name,
          title: data.title,
          field: data.field,
          avatar: data.avatar,
          description: data.description,
          personality: data.personality,
          greeting: data.greeting,
          systemPrompt: data.system_prompt,
          promptPrefix: data.prompt_prefix,
          active: data.active,
          createdAt: data.created_at,
          updatedAt: data.updated_at
        };
      }
    } catch (error) {
      console.error(`Error in saveExpert for expert with ID ${expert.id}:`, error);
      return expert;
    }
  },
  
  /**
   * Xóa chuyên gia
   * @param id ID của chuyên gia cần xóa
   * @returns true nếu xóa thành công, false nếu có lỗi
   */
  async deleteExpert(id: string): Promise<boolean> {
    try {
      console.log(`Deleting expert with ID: ${id}`);
      
      // Kiểm tra xem ID có phải là UUID hợp lệ không
      const isValidUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(id);
      
      if (!isValidUUID) {
        console.warn(`Cannot delete expert with ID ${id} as it is not a valid UUID`);
        return false;
      }
      
      const { error } = await supabase
        .from('experts')
        .delete()
        .eq('id', id);
      
      if (error) {
        console.error(`Error deleting expert with ID ${id}:`, error);
        return false;
      }
      
      // Xóa cache cho expert này
      this.clearExpertCache(id);
      
      return true;
    } catch (error) {
      console.error(`Error in deleteExpert for ID ${id}:`, error);
      return false;
    }
  }
}; 